import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";

import { useRestClientContext } from "../contexts/RestClientContext";
import { useTeamContext } from "../contexts/TeamContext";
import Property from "../rest/Property";
import { EpcCertificateResult, EpcCertificateSearchResponse } from "../rest/responses/EpcCertificate";
import { Accessor, DataIntegritySource, PropertyAttribute, Scalar } from "../types/types";
import useUpdateTrigger from "./useUpdateTrigger";

interface EpcCompareUpdateProps {
	property: Property;
	onSuccess: () => void;
}

/**
 * Finds a property on the EPC register and saves the certificate.
 *
 * The API searches by UPRN where we hold one, otherwise by address and postcode, falling back to the postcode alone.
 * The search may return several possibilities, in which case the user picks.
 */
export default function useEpcCompareUpdate({ property, onSuccess }: EpcCompareUpdateProps) {
	const client = useRestClientContext();
	const team = useTeamContext();
	const { trigger, triggerUpdate } = useUpdateTrigger();

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string>();
	const [results, setResults] = useState<EpcCertificateResult[]>([]);
	const [selectedRrn, setSelectedRrn] = useState<string>();
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState<string>();

	useEffect(() => {
		const controller = new AbortController();

		setLoading(true);
		setError(undefined);

		client
			.searchEpcCertificates(team.id, property.id, controller.signal)
			.then((response: Response) => response.json() as Promise<EpcCertificateSearchResponse>)
			.then(({ results }) => {
				setResults(results);
				// Preselect the APIs best guess. It is omitted for a single result, or it might not be found, so fall
				// back to the first.
				setSelectedRrn(results.find((result) => result.is_best_match)?.rrn ?? results[0]?.rrn);
				setLoading(false);
			})
			.catch((error: Error) => {
				if (controller.signal.aborted) return;

				setError(error.message);
				setLoading(false);
			});

		return () => controller.abort();
	}, [client, team, property, trigger]);

	const selected = useMemo(() => results.find((result) => result.rrn === selectedRrn), [results, selectedRrn]);

	const save = () => {
		if (!selected) return;

		// Everything below came from the EPC register, so mark it as such in the data integrity table.
		const integrity: Partial<Record<Accessor | PropertyAttribute, DataIntegritySource>> = {
			rrn: DataIntegritySource.EPC,
		};

		const data: Partial<Record<Accessor | PropertyAttribute, Scalar>> = {
			rrn: selected.rrn,
		};

		// Also save the o_uprn if it is not already recorded.
		if ("o_uprn" in property.data && !property.data.o_uprn && selected.uprn) {
			data.o_uprn = selected.uprn;
			integrity.o_uprn = DataIntegritySource.EPC;
		}

		data["data_integrity.integrity"] = JSON.stringify(integrity);

		setSaving(true);
		setSaveError(undefined);

		return client.v3
			.updateProperty(team, property, data)
			.then(() => onSuccess())
			.catch((error: Error) => setSaveError(error.message))
			.finally(() => setSaving(false));
	};

	return {
		loading,
		error,
		results,
		selected,
		setSelectedRrn,
		// Allows differentiation between loading results and loading when saved.
		saving,
		saveError,
		save,
		refresh: triggerUpdate,
	};
}
